import { useState, useMemo } from 'react';
import {
  Container,
  Paper,
  Title,
  Stack,
  Group,
  Button,
  Tabs,
  Text,
  Table,
  Badge,
  ActionIcon,
  Menu,
  TextInput,
  Box,
  Center,
  Loader,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconPlus,
  IconClipboardList,
  IconHistory,
  IconDots,
  IconEdit,
  IconTrash,
  IconEye,
  IconSearch,
  IconFilter,
} from '@tabler/icons-react';
import { AuditMobileCard } from './components/AuditMobileCard';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuditEvents, useDeleteAuditEvent } from '@/hooks/api/useAudits';
import {
  useSavedFilters,
  useCreateSavedFilter,
  useDeleteSavedFilter,
  useToggleFavoriteSavedFilter,
} from '@/hooks/api/useSavedFilters';
import { AuditForm } from './components/AuditForm';
import { AuditFilterContent, type AuditFilter } from './components/AuditFilterContent';
import { getAuditFilterPresets } from './components/AuditFilterPresets';
import { FilterModal } from '@/components/ui/FilterModal';
import { notifications } from '@mantine/notifications';
import type { AssetAuditEvent } from '@/types/models';

export function AuditsPage() {
  const { organizationId } = useOrganization();
  const { data: auditEvents = [], isLoading } = useAuditEvents(organizationId);
  const deleteAuditEvent = useDeleteAuditEvent();

  // Saved filters
  const { data: savedFilters = [] } = useSavedFilters(organizationId, 'audits');
  const createSavedFilter = useCreateSavedFilter();
  const deleteSavedFilter = useDeleteSavedFilter();
  const toggleFavorite = useToggleFavoriteSavedFilter();

  const [auditFormOpened, setAuditFormOpened] = useState(false);
  const [filterOpened, setFilterOpened] = useState(false);
  const [editingAudit, setEditingAudit] = useState<AssetAuditEvent | null>(null);
  const [activeTab, setActiveTab] = useState<string>('active');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<AuditFilter>({});

  const handleCreateAudit = () => {
    setEditingAudit(null);
    setAuditFormOpened(true);
  };

  const handleEditAudit = (audit: AssetAuditEvent) => {
    setEditingAudit(audit);
    setAuditFormOpened(true);
  };

  const handleDeleteAudit = async (id: string) => {
    modals.openConfirmModal({
      title: 'Delete Audit',
      children: (
        <Text size="sm">
          Are you sure you want to delete this audit? This will also remove all associated audit items. This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteAuditEvent.mutateAsync(id);
          notifications.show({
            title: 'Audit Deleted',
            message: 'The audit has been deleted successfully',
            color: 'green',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: 'Failed to delete audit',
            color: 'red',
          });
        }
      },
    });
  };

  const getStatusColor = (status: AssetAuditEvent['status']) => {
    switch (status) {
      case 'draft':
        return 'gray';
      case 'in_progress':
        return 'blue';
      case 'completed':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Apply filters to audits
  const filteredAudits = useMemo(() => {
    return auditEvents.filter((audit) => {
      // Status filter
      if (filters.status && audit.status !== filters.status) return false;

      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        if (!audit.name.toLowerCase().includes(searchLower)) return false;
      }

      // Local search bar filter
      if (search) {
        const searchLower = search.toLowerCase();
        if (!audit.name.toLowerCase().includes(searchLower)) return false;
      }

      // Date range filter
      if (filters.dateRange?.start || filters.dateRange?.end) {
        const auditDate = audit.audit_date ? new Date(audit.audit_date) : null;
        if (auditDate) {
          if (filters.dateRange.start && auditDate < filters.dateRange.start) return false;
          if (filters.dateRange.end && auditDate > filters.dateRange.end) return false;
        }
      }

      return true;
    });
  }, [auditEvents, filters, search]);

  const activeAudits = filteredAudits.filter(
    (a) => a.status === 'draft' || a.status === 'in_progress'
  );
  const completedAudits = filteredAudits.filter(
    (a) => a.status === 'completed' || a.status === 'cancelled'
  );

  const auditsToShow = activeTab === 'active' ? activeAudits : completedAudits;

  // Count active filters
  const activeFilterCount = Object.keys(filters).reduce((count, key) => {
    const value = filters[key as keyof AuditFilter];
    if (value === undefined || value === null) return count;
    if (typeof value === 'object' && !Array.isArray(value)) {
      const hasValues = Object.values(value).some(v => v !== null && v !== undefined);
      return hasValues ? count + 1 : count;
    }
    return count + 1;
  }, 0);

  const handleSaveFilter = (name: string, description?: string) => {
    createSavedFilter.mutate({
      organization_id: organizationId,
      name,
      description,
      entity_type: 'audits',
      filter_config: filters,
      is_favorite: true,
    });
  };

  const handleLoadFilter = (loadedFilters: AuditFilter) => {
    setFilters(loadedFilters);
  };

  const handleToggleFavorite = (filterId: string) => {
    const filter = savedFilters.find(f => f.id === filterId);
    if (filter) {
      toggleFavorite.mutate({
        id: filterId,
        isFavorite: filter.is_favorite,
        organizationId,
        entityType: 'audits',
      });
    }
  };

  const handleDeleteFilter = (filterId: string) => {
    modals.openConfirmModal({
      title: 'Delete Saved Filter',
      children: (
        <Text size="sm">
          Are you sure you want to delete this saved filter? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteSavedFilter.mutate({
          id: filterId,
          organizationId,
          entityType: 'audits',
        });
      },
    });
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleApplyFilters = () => {
    // Filters are already applied via filteredAudits
  };

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>Audits</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Manage asset audits and track inventory verification
            </Text>
          </div>

          <Button leftSection={<IconPlus size={16} />} onClick={handleCreateAudit}>
            Create Audit
          </Button>
        </Group>

        <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'active')}>
          <Tabs.List>
            <Tabs.Tab value="active" leftSection={<IconClipboardList size={16} />}>
              Active Audits ({activeAudits.length})
            </Tabs.Tab>
            <Tabs.Tab value="completed" leftSection={<IconHistory size={16} />}>
              Completed ({completedAudits.length})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={activeTab} pt="md">
            <Stack gap="md">
              {/* Search and Filter Bar */}
              <Paper p="md" withBorder>
                <Group>
                  <TextInput
                    placeholder="Search audits..."
                    leftSection={<IconSearch size={16} />}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="light"
                    leftSection={<IconFilter size={16} />}
                    onClick={() => setFilterOpened(true)}
                    rightSection={
                      activeFilterCount > 0 ? (
                        <Badge size="sm" variant="filled" color="blue" circle>
                          {activeFilterCount}
                        </Badge>
                      ) : undefined
                    }
                  >
                    Advanced Filters
                  </Button>
                </Group>
              </Paper>

              {activeFilterCount > 0 && (
                <Text size="sm" c="dimmed">
                  Showing {filteredAudits.length} of {auditEvents.length} audits
                </Text>
              )}

              {/* Mobile card view */}
              <Box hiddenFrom="sm">
                {isLoading ? (
                  <Center py="xl">
                    <Loader />
                  </Center>
                ) : auditsToShow.length === 0 ? (
                  <Paper withBorder p="xl">
                    <Stack align="center">
                      <IconClipboardList size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />
                      <Text c="dimmed" ta="center">
                        {activeTab === 'active'
                          ? 'No active audits. Create one to get started.'
                          : 'No completed audits yet.'}
                      </Text>
                    </Stack>
                  </Paper>
                ) : (
                  <Paper withBorder style={{ overflow: 'hidden' }}>
                    {auditsToShow.map((audit) => (
                      <AuditMobileCard
                        key={audit.id}
                        audit={audit}
                        onView={(a) => {
                          window.location.href = `/audits/${a.id}`;
                        }}
                      />
                    ))}
                  </Paper>
                )}
              </Box>

              {/* Desktop table view */}
              <Box visibleFrom="sm">
                <Paper p="md" withBorder>
                  {isLoading ? (
                    <Center py="xl">
                      <Loader />
                    </Center>
                  ) : auditsToShow.length === 0 ? (
                    <Stack align="center" py="xl">
                      <IconClipboardList size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />
                      <Text c="dimmed">
                        {activeTab === 'active'
                          ? 'No active audits. Create one to get started.'
                          : 'No completed audits yet.'}
                      </Text>
                    </Stack>
                  ) : (
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Name</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th>Audit Date</Table.Th>
                          <Table.Th>Due By</Table.Th>
                          <Table.Th>Created</Table.Th>
                          <Table.Th></Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {auditsToShow.map((audit) => (
                          <Table.Tr key={audit.id}>
                            <Table.Td>
                              <Text fw={500}>{audit.name}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge color={getStatusColor(audit.status)} variant="light">
                                {audit.status.replace('_', ' ')}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {audit.audit_date
                                ? new Date(audit.audit_date).toLocaleDateString()
                                : '-'}
                            </Table.Td>
                            <Table.Td>
                              {audit.due_by_date
                                ? new Date(audit.due_by_date).toLocaleDateString()
                                : '-'}
                            </Table.Td>
                            <Table.Td>
                              {new Date(audit.created_at).toLocaleDateString()}
                            </Table.Td>
                            <Table.Td>
                              <Group gap="xs" justify="flex-end">
                                <ActionIcon
                                  variant="subtle"
                                  color="blue"
                                  onClick={() => {
                                    window.location.href = `/audits/${audit.id}`;
                                  }}
                                  title="View/Review"
                                >
                                  <IconEye size={16} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  onClick={() => handleEditAudit(audit)}
                                  title="Edit"
                                >
                                  <IconEdit size={16} />
                                </ActionIcon>
                                <Menu position="bottom-end" shadow="md">
                                  <Menu.Target>
                                    <ActionIcon variant="subtle" color="gray">
                                      <IconDots size={16} />
                                    </ActionIcon>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    <Menu.Item
                                      leftSection={<IconTrash size={16} />}
                                      color="red"
                                      onClick={() => handleDeleteAudit(audit.id)}
                                    >
                                      Delete
                                    </Menu.Item>
                                  </Menu.Dropdown>
                                </Menu>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Paper>
              </Box>
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <AuditForm
          opened={auditFormOpened}
          onClose={() => {
            setAuditFormOpened(false);
            setEditingAudit(null);
          }}
          audit={editingAudit}
        />

        <FilterModal
          opened={filterOpened}
          onClose={() => setFilterOpened(false)}
          title="Filter Audits"
          filters={filters}
          onFiltersChange={setFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onSave={handleSaveFilter}
          onLoadSaved={handleLoadFilter}
          onToggleFavorite={handleToggleFavorite}
          onDeleteSaved={handleDeleteFilter}
          presets={getAuditFilterPresets()}
          savedFilters={savedFilters.map(f => ({
            id: f.id,
            name: f.name,
            description: f.description,
            filters: f.filter_config,
            isFavorite: f.is_favorite,
            createdAt: f.created_at,
          }))}
          activeFilterCount={activeFilterCount}
        >
          <AuditFilterContent filters={filters} onFiltersChange={setFilters} />
        </FilterModal>
      </Stack>
    </Container>
  );
}
